import { useState } from "react";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Languages, Globe } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

/**
 * Adds a "Translate" affordance to any chat message. Click → pick language →
 * fetches translation from /api/messages/{id}/translate, displays it inline
 * under the original. Caches the translation per-mount.
 */
const LANGS = [
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "pt", name: "Portuguese" },
  { code: "ht", name: "Haitian Creole" },
  { code: "vi", name: "Vietnamese" },
  { code: "zh", name: "Mandarin Chinese" },
  { code: "tl", name: "Tagalog" },
  { code: "ar", name: "Arabic" },
  { code: "ru", name: "Russian" },
  { code: "de", name: "German" },
  { code: "it", name: "Italian" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
  { code: "hi", name: "Hindi" },
  { code: "pa", name: "Punjabi" },
  { code: "ur", name: "Urdu" },
  { code: "tr", name: "Turkish" },
  { code: "pl", name: "Polish" },
  { code: "nl", name: "Dutch" },
  { code: "sw", name: "Swahili" },
  { code: "am", name: "Amharic" },
  { code: "so", name: "Somali" },
  { code: "fa", name: "Persian" },
  { code: "th", name: "Thai" },
  { code: "id", name: "Indonesian" },
  { code: "en", name: "English" },
];

export default function TranslateButton({ messageId, theme = "light" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { lang, name, text }

  const translate = async (lang) => {
    setOpen(false);
    setLoading(true);
    try {
      const r = await api.post(`/messages/${messageId}/translate`, { target_lang: lang.code });
      setResult({ code: lang.code, name: lang.name, text: r.data.translated });
    } catch (e) { toast.error(getApiError(e)); }
    finally { setLoading(false); }
  };

  const linkBase = theme === "dark"
    ? "text-white/80 hover:text-white"
    : "text-[color:var(--text-secondary)] hover:text-[color:var(--fire-red)]";
  const blockBase = theme === "dark"
    ? "border-white/20 bg-white/10 text-white"
    : "border-[color:var(--bright-green)]/30 bg-[color:var(--bright-green)]/5 text-[color:var(--text-primary)]";

  return (
    <div className="mt-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={loading}
            className={`text-[10px] inline-flex items-center gap-1 underline-offset-2 hover:underline transition-opacity ${linkBase} ${loading ? "opacity-50" : ""}`}
            data-testid={`translate-btn-${messageId}`}
          >
            <Languages className="h-3 w-3" />
            {loading ? "Translating…" : result ? "Translate to another language" : "Translate"}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-0 max-h-64 overflow-y-auto" data-testid={`translate-popover-${messageId}`}>
          <ul className="py-1 text-sm">
            {LANGS.map((l) => (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => translate(l)}
                  className="w-full text-left px-3 py-1.5 hover:bg-[color:var(--bg-secondary)] flex items-center gap-2"
                  data-testid={`translate-lang-${l.code}-${messageId}`}
                >
                  <Globe className="h-3 w-3 text-[color:var(--text-secondary)]" />{l.name}
                </button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>

      {result && (
        <div
          className={`mt-2 rounded-md border px-2 py-1.5 text-xs leading-relaxed ${blockBase}`}
          data-testid={`translate-result-${messageId}`}
        >
          <div className="text-[9px] uppercase tracking-[0.2em] mb-1 opacity-70">{result.name}</div>
          <p className="whitespace-pre-wrap break-words">{result.text}</p>
        </div>
      )}
    </div>
  );
}
